import React, { useMemo } from 'react'
import StudioCard from '../../components/card/studio_card'
import Link from '../../components/router/link'
import Button from '../../components/button'
import { PopupNotificationType, useShowNotification } from '../../contexts/popup_notification'
import Modal from '../../components/modal'
import Typography from '../../components/typography'
import Box from '../../components/box'
import { StudioStatus } from '../../apilibrary/types/studio'

export interface StudioModelCardProps {
  id: string
  status: StudioStatus
  title: string
  coverPictureUrl: string
  rate: number
  maxOccupancy: number
}

export interface SpaceItemProps extends StudioModelCardProps {
  editActions: {
    allowEdit?: boolean
    handleDelist?: () => Promise<void>
    handleEnlist?: () => Promise<void>
  }
}

const SpaceItem: React.FC<SpaceItemProps> = ({
  id,
  status,
  title,
  coverPictureUrl,
  rate,
  maxOccupancy,
  editActions: {
    allowEdit,
    handleDelist: handleDelistStudio,
    handleEnlist: handleEnlistStudio
  }
}) => {
  const [promptConfirmDelistOpen, setPromptConfirmDelistOpen] = React.useState(false);
  const [loadingStudioAction, setLoadingStudioAction] = React.useState(false);
  const showNotification = useShowNotification();

  const confirmDelistModal = useMemo(() => {
    const handleDelist = async (): Promise<void> => {
      try {
        setLoadingStudioAction(true);
        if (!handleDelistStudio) {
          throw new Error('handleDelist is required when allowEdit is true');
        }
        await handleDelistStudio();
        showNotification({
          message: 'Studio delisted successfully',
          type: PopupNotificationType.SUCCESS
        });
      } catch (error) {
        console.log(error);
        let message = 'Something went wrong. Please try again later.';
        if (error instanceof Error && error.message) {
          message = error.message;
        }
        showNotification({
          message,
          type: PopupNotificationType.ERROR
        });
      } finally {
        setLoadingStudioAction(false);
        setPromptConfirmDelistOpen(false);
      }
    };

    return (
      <Modal
        sx={{
          marginTop: '40%',
          width: '400px'
        }}
        open={promptConfirmDelistOpen}
        onClose={() => { setPromptConfirmDelistOpen(false); }}
        title={'Confirm Delist'}
      >
        <>
          <Typography gutterBottom>Are you sure you want to delist this studio?</Typography>
          <Box display={'flex'} gap={2}>
            <Button disabled={loadingStudioAction} variant='outlined' color='secondary' onClick={() => { setPromptConfirmDelistOpen(false); }}>Cancel</Button>
            <Button disabled={loadingStudioAction} variant='outlined' color='error' onClick={handleDelist}>Delist</Button>
          </Box>
        </>
      </Modal>
    )
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingStudioAction, promptConfirmDelistOpen, showNotification])

  const statusActionButtons = useMemo(() => {
    const handleEnlist = async (): Promise<void> => {
      try {
        if (!handleEnlistStudio) {
          throw new Error('handleEnlist is required when allowEdit is true');
        }
        await handleEnlistStudio();
        showNotification({
          message: 'Studio enlisted successfully',
          type: PopupNotificationType.SUCCESS
        });
      } catch (error) {
        console.log(error);
        let message = 'Something went wrong. Please try again later.';
        if (error instanceof Error && error.message) {
          message = error.message;
        }
        showNotification({
          message,
          type: PopupNotificationType.ERROR
        });
      }
    }

    if (status === StudioStatus.DELISTED) {
      return (
        <Button onClick={handleEnlist} color='success'>Enlist</Button>
      )
    }
    if (status === StudioStatus.ACTIVE) {
      return (
        <Button onClick={() => { setPromptConfirmDelistOpen(true); }} color='warning'>Delist</Button>
      )
    }
    if (status === StudioStatus.PENDING_RESOLVE_ACTIONS) {
      return (
        <Button color='warning' disabled={true}>Pending</Button>
      )
    }
  }, [handleEnlistStudio, showNotification, status]);

  return (
    <>
      {confirmDelistModal}
      <StudioCard
        navigateTo={`/studio/${id}`}
        title={title}
        coverPictureUrl={coverPictureUrl}
        rate={rate}
        maxOccupancy={maxOccupancy}
        actions={allowEdit
          ? (
            <>
              <Link to={`/studio/${id}/edit`}>
                <Button color='primary'>Edit</Button>
              </Link>
              {statusActionButtons}
            </>
            )
          : undefined
        }
      />
    </>
  )
}

export default SpaceItem
